import { FC, useCallback, useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';

import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type { TableState } from 'components/data-table/types';
import { useBlobDownload } from 'lib/hooks/useBlobDownload';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';

import { SlotTable } from '../slot-table';
import type {
  DraftSlotsQueryParams,
  DraftSlotsResponse,
  FlatSlotRow,
} from '../types';
import {
  draftSlotFilterStateToParams,
  flattenDraftSlots,
  sortParamsFromState,
} from '../utils';

/** Props for {@link Step2SlotsTable}. */
export interface Step2SlotsTableProps {
  /** Active draft id. The fetch fires on mount/draftId-change. */
  draftId: string;
}

/**
 * Read-only slots query for Step 2's Slot Allocations accordion. Mirrors
 * Step 1's `draftSlotsQueryAtom` but with its own abort controller — so
 * paging Step 1's slot table can't cancel Step 2's in-flight fetch (and
 * vice versa) — and intentionally skips the `currentDraftSummaryAtom`
 * write. Step 1's summary panel reads the universe view; overwriting it
 * from a `selected: true` fetch would shift its breakdowns to selected-
 * only, which is a different question.
 */
const [, draftSlotsReadOnlyQueryLoadableAtom] = mutationAtomWithLoadable<
  DraftSlotsResponse,
  [DraftSlotsQueryParams]
>(
  (() => {
    let controller: AbortController | null = null;
    return async (_get, _set, params: DraftSlotsQueryParams) => {
      controller?.abort();
      controller = new AbortController();
      const { draftId, ...body } = params;
      const url = `/queries/packages/drafts/${draftId}/slots`;
      const response = await request.post<
        DraftSlotsResponse,
        { errors: string[] }
      >(url, body, controller);
      if (response.type === 'success') return response.data;
      if (response.type === 'abort') return new Promise<never>(() => {});
      if (response.type === 'error' && response.data?.errors?.length) {
        throw new Error(response.data.errors.join(', '));
      }
      throw new Error('Failed to fetch draft slots');
    };
  })()
);

/**
 * Step 2 read-only slots table. Shows only allocations currently selected
 * on the draft (BE-filtered via `selected: true`). No selection column,
 * no pin column, no PATCH wiring — review-screen presentation only.
 *
 * Reuses {@link SlotTable} for columns, expandable subtract sub-row, and
 * the DataTable shell. Verification (NRI/WI) cells stay interactive — that
 * state lives in `verificationStatesAtom` which is conceptually a per-slot
 * annotation, not a selection signal.
 *
 * @param {Step2SlotsTableProps} props - draft id
 * @return {JSX.Element} The read-only slots table
 */
export const Step2SlotsTable: FC<Step2SlotsTableProps> = ({ draftId }) => {
  const [queryState, fetchSlots] = useAtom(draftSlotsReadOnlyQueryLoadableAtom);

  const { triggerDownload: handleExport } = useBlobDownload({
    url: `/queries/packages/drafts/${draftId}/slots/export`,
    // Fallback only — useBlobDownload prefers the server's Content-Disposition
    // filename, which the controller sets to `draft-<id>-slot_allocations.csv`.
    filename: `draft-${draftId}-slot_allocations.csv`,
    body: { query: { selected: true } },
  });

  const loadData = useCallback(
    (params: TableState) => {
      fetchSlots({
        draftId,
        // `selected: true` is the read-only contract — baked in here, not
        // exposed as a prop, so callers can't accidentally widen the scope.
        selected: true,
        page: params.page,
        pageSize: params.pageSize,
        text: params.globalFilter || undefined,
        ...sortParamsFromState(params.sorting),
        ...draftSlotFilterStateToParams(params.columnFilters),
      });
    },
    [draftId, fetchSlots]
  );

  // DataTable doesn't self-fire `loadData` on mount in paginated mode;
  // kick off the initial fetch when the draft id changes. Match Step 1's
  // default-sort key so the read-only review opens in the same order.
  useEffect(() => {
    loadData({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      globalFilter: '',
      sorting: [{ id: 'slotId', desc: false }],
      columnFilters: [],
      selection: { selectAll: false, toggledNodes: [] },
      expanded: {},
    });
  }, [draftId, loadData]);

  const data = queryState.state === 'hasData' ? queryState.data : null;
  const rows = useMemo<FlatSlotRow[]>(
    () => (data ? flattenDraftSlots(data.slots) : []),
    [data]
  );
  const totalCount = data?.meta.totalCount;
  const isLoading = queryState.state === 'loading';
  const errorMessage: string | null =
    queryState.state === 'hasError'
      ? queryState.error instanceof Error
        ? queryState.error.message
        : 'An unexpected error occurred'
      : null;

  return (
    <SlotTable
      rows={rows}
      totalCount={totalCount}
      isLoading={isLoading}
      loadData={loadData}
      errorMessage={errorMessage}
      onExport={handleExport}
    />
  );
};
